/**
 * jsPDF-based PDF generator for the Lieferanten-Anfrage export.
 *
 * Mirrors generateKundenKonformitaetPdf but anchored on supplier: prints
 * a German questionnaire listing the components we source from the
 * supplier plus an empty "Antwort"-column for them to fill in.
 */

import { jsPDF } from "jspdf"
import { generateRegulatoryNotes } from "../../../components/conformity/conformityPdf"
import type { LieferantenAbfrageHtmlData } from "../html/lieferanten_abfrage"

const CANDIDATE_LIST_VERSION = "31. Aktualisierung"
const CANDIDATE_LIST_DATE = "2024-06-27"
const CANDIDATE_LIST_ENTRIES = 240

function sanitize(s: string): string {
  return (s ?? "").replace(/[^a-zA-Z0-9_-]/g, "_").slice(0, 60)
}

type FlatRow = {
  componentName: string
  lastDeclaredSvhcPresent: string
  casNumber: string
  substanceName: string
}

export function generateLieferantenAbfragePdf(
  data: LieferantenAbfrageHtmlData,
): void {
  const { supplier } = data
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" })
  const pageW = doc.internal.pageSize.getWidth()
  const pageH = doc.internal.pageSize.getHeight()
  const margin = 20
  const contentW = pageW - 2 * margin
  let y = margin

  const addFooter = () => {
    const total = doc.getNumberOfPages()
    for (let i = 1; i <= total; i++) {
      doc.setPage(i)
      doc.setFontSize(7)
      doc.setTextColor(150)
      doc.text(
        `turnus.ai QMS · Vertraulich · Seite ${i} von ${total}`,
        margin,
        pageH - 10,
      )
      doc.text(
        `REACH-Lieferantenanfrage · ${supplier.supplierName} · ${data.creationDate}`,
        pageW - margin,
        pageH - 10,
        { align: "right" },
      )
    }
  }

  const ensureSpace = (needed: number) => {
    if (y + needed > pageH - 20) {
      doc.addPage()
      y = margin
    }
  }

  // --- 1. Title ---------------------------------------------------------------
  doc.setFontSize(18)
  doc.setTextColor(30)
  doc.setFont("helvetica", "bold")
  doc.text("Lieferantenanfrage SVHC", margin, y)
  y += 7
  doc.setFontSize(10)
  doc.setFont("helvetica", "normal")
  doc.setTextColor(100)
  doc.text("gemäß Verordnung (EG) Nr. 1907/2006 (REACH), Artikel 33", margin, y)
  y += 4
  doc.setDrawColor(200)
  doc.setLineWidth(0.3)
  doc.line(margin, y, pageW - margin, y)
  y += 8

  // --- 2. Meta block (two columns) --------------------------------------------
  const col1X = margin
  const col2X = margin + contentW / 2 + 5
  const metaLineH = 5

  const metaLeft: [string, string][] = [
    ["Aussteller", data.issuer || "—"],
    ["Adresse", data.issuerAddress || "—"],
    ["Erstellungsdatum", data.creationDate],
    ["Antwort erbeten bis", data.responseDueDate || "—"],
  ]
  const metaRight: [string, string][] = [
    ["Empfänger", supplier.supplierName],
    ["Lieferanten-Nr.", supplier.supplierId],
    [
      "Ansprechpartner",
      [supplier.contactName, supplier.contactPosition]
        .filter(Boolean)
        .join(", ") || "—",
    ],
    [
      "Kandidatenliste",
      `${CANDIDATE_LIST_VERSION} (${CANDIDATE_LIST_ENTRIES} Einträge, ${CANDIDATE_LIST_DATE})`,
    ],
  ]

  for (let i = 0; i < metaLeft.length; i++) {
    doc.setFont("helvetica", "normal")
    doc.setFontSize(8)
    doc.setTextColor(120)
    doc.text(metaLeft[i][0], col1X, y)
    doc.text(metaRight[i][0], col2X, y)
    y += metaLineH * 0.6
    doc.setFont("helvetica", "bold")
    doc.setFontSize(9)
    doc.setTextColor(50)
    const leftLines = doc.splitTextToSize(metaLeft[i][1], contentW / 2 - 5)
    const rightLines = doc.splitTextToSize(metaRight[i][1], contentW / 2 - 5)
    doc.text(leftLines, col1X, y)
    doc.text(rightLines, col2X, y)
    y += metaLineH
  }

  y += 2
  doc.setDrawColor(200)
  doc.setLineWidth(0.3)
  doc.line(margin, y, pageW - margin, y)
  y += 8

  // --- 3. Anliegen ------------------------------------------------------------
  ensureSpace(20)
  doc.setFontSize(12)
  doc.setFont("helvetica", "bold")
  doc.setTextColor(30)
  doc.text("Anliegen", margin, y)
  y += 7

  const askLines = [
    `Sehr geehrte Damen und Herren,`,
    ``,
    `im Rahmen unserer REACH-Konformitätspflichten gemäß Verordnung (EG) Nr. 1907/2006, ` +
      `Artikel 33, bitten wir Sie um eine aktualisierte Auskunft zu den nachfolgend ` +
      `aufgeführten Bauteilen, die wir von ${supplier.supplierName} beziehen.`,
    ``,
    `Bitte bestätigen oder aktualisieren Sie für jedes Bauteil, ob besonders besorgniserregende ` +
      `Stoffe (SVHC) gemäß der aktuellen ECHA-Kandidatenliste in einer Konzentration von mehr als ` +
      `0,1 Massenprozent enthalten sind. Wir bitten um Rückmeldung bis spätestens ` +
      `${data.responseDueDate || "—"}.`,
  ]

  doc.setFontSize(9)
  doc.setFont("helvetica", "normal")
  doc.setTextColor(60)
  for (const raw of askLines) {
    const wrapped = doc.splitTextToSize(raw, contentW)
    for (const w of wrapped) {
      ensureSpace(5)
      doc.text(w, margin, y)
      y += 4.5
    }
    y += 1
  }
  y += 4

  // --- 4. Components table ----------------------------------------------------
  const flatRows: FlatRow[] = supplier.components.flatMap((c) => {
    if (c.rows.length === 0) {
      return [
        {
          componentName: c.componentName,
          lastDeclaredSvhcPresent: c.lastDeclaredSvhcPresent,
          casNumber: "—",
          substanceName: "—",
        },
      ]
    }
    return c.rows.map((r) => ({
      componentName: c.componentName,
      lastDeclaredSvhcPresent: c.lastDeclaredSvhcPresent,
      casNumber: r.casNumber,
      substanceName: r.substanceName,
    }))
  })

  ensureSpace(20)
  doc.setFontSize(12)
  doc.setFont("helvetica", "bold")
  doc.setTextColor(30)
  doc.text("Bauteile und letzte Angabe", margin, y)
  y += 8

  const headers = [
    "Bauteil",
    "Letzte Angabe",
    "CAS-Nr.",
    "Stoffname",
    "Antwort",
  ]
  const colWidths = [40, 26, 26, 40, 38]

  // Header row
  doc.setFillColor(45, 45, 50)
  doc.rect(margin, y - 4, contentW, 7, "F")
  doc.setFontSize(7)
  doc.setFont("helvetica", "bold")
  doc.setTextColor(255)
  let cx = margin + 2
  for (let i = 0; i < headers.length; i++) {
    doc.text(headers[i], cx, y)
    cx += colWidths[i]
  }
  y += 5

  // Data rows
  doc.setFont("helvetica", "normal")
  doc.setFontSize(7.5)
  const lineH = 3.5

  if (flatRows.length === 0) {
    ensureSpace(8)
    doc.setTextColor(120)
    doc.text("Keine Bauteile hinterlegt.", margin + 2, y)
    y += 6
  }

  flatRows.forEach((row, idx) => {
    const vals = [
      row.componentName,
      row.lastDeclaredSvhcPresent || "—",
      row.casNumber,
      row.substanceName,
      "", // answer column left blank for supplier to fill
    ]
    const cellLines = vals.map((v, i) =>
      doc.splitTextToSize(v || "", colWidths[i] - 3),
    )
    const maxLines = Math.max(...cellLines.map((l) => l.length), 1)
    const rowH = Math.max(8, maxLines * lineH + 3)

    ensureSpace(rowH + 2)
    if (idx % 2 === 0) {
      doc.setFillColor(245, 245, 248)
      doc.rect(margin, y - 3.5, contentW, rowH, "F")
    }
    doc.setTextColor(60)
    cx = margin + 2
    for (let i = 0; i < vals.length; i++) {
      let lineY = y
      for (const line of cellLines[i]) {
        doc.text(line, cx, lineY)
        lineY += lineH
      }
      cx += colWidths[i]
    }
    // Draw the empty answer-cell border to make it look like a form field.
    const answerX =
      margin + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3] + 1
    const answerW = colWidths[4] - 2
    doc.setDrawColor(150)
    doc.setLineWidth(0.2)
    doc.rect(answerX, y - 2.5, answerW, rowH - 2)
    y += rowH
  })
  y += 6

  // --- 5. Regulatory notes ----------------------------------------------------
  ensureSpace(20)
  doc.setFontSize(12)
  doc.setFont("helvetica", "bold")
  doc.setTextColor(30)
  doc.text("Regulatorische Hinweise", margin, y)
  y += 7
  doc.setFontSize(9)
  doc.setFont("helvetica", "normal")
  doc.setTextColor(60)

  const noteLines = generateRegulatoryNotes()
    .split("\n")
    .filter((l) => l.trim())
  for (const noteLine of noteLines) {
    const wrapped = doc.splitTextToSize(noteLine, contentW)
    for (const wl of wrapped) {
      ensureSpace(5)
      doc.text(wl, margin, y)
      y += 4.5
    }
    y += 2
  }
  y += 6

  // --- 6. Signature line for the supplier -------------------------------------
  ensureSpace(30)
  doc.setDrawColor(80)
  doc.setLineWidth(0.4)
  doc.line(margin, y, margin + 72, y)
  y += 5
  doc.setFontSize(10)
  doc.setFont("helvetica", "bold")
  doc.setTextColor(30)
  doc.text(supplier.contactName || "—", margin, y)
  y += 5
  doc.setFont("helvetica", "normal")
  doc.setTextColor(120)
  doc.setFontSize(9)
  doc.text(supplier.contactPosition || "", margin, y)
  y += 5
  if (supplier.contactLocation || data.signatoryDate) {
    doc.text(
      `${supplier.contactLocation || ""}${supplier.contactLocation && data.signatoryDate ? ", " : ""}${data.signatoryDate || ""}`,
      margin,
      y,
    )
  }

  addFooter()

  doc.save(
    `REACH_Lieferantenanfrage_${sanitize(supplier.supplierName || supplier.supplierId)}.pdf`,
  )
}
